"use client";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";
const URB = "var(--font-urbanist), sans-serif";

interface CompletedBannerProps {
  taskTitle: string;
  agentName: string;
  cost: number;
  rating: number | null;
  onNewTask: () => void;
  onRetry?: () => void;
}

const CONFETTI_COLORS = ["#8635F6", "#F6AE2D", "#4ADE80", "#86BBD8"];

export default function CompletedBanner({
  taskTitle,
  agentName,
  cost,
  rating,
  onNewTask,
  onRetry,
}: CompletedBannerProps) {
  const positive = (rating ?? 0) >= 4;

  return (
    <div
      className="app-anim-fade-up"
      style={{
        width: "100%",
        maxWidth: 720,
        background: "rgba(30,30,30,0.9)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${positive ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {positive && <Confetti />}

      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          className="app-anim-check-bounce"
          style={{
            width: 40,
            height: 40,
            borderRadius: 9999,
            background: positive ? "rgba(74,222,128,0.18)" : "rgba(134,187,216,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12.5L10 17.5L19 7.5"
              stroke={positive ? "#4ADE80" : "#86BBD8"}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "22px",
              color: "#F2F0ED",
              margin: 0,
            }}
          >
            {positive ? "Task completed successfully!" : "Task completed — we'll do better next time"}
          </p>
          <p
            style={{
              fontFamily: PJK,
              fontWeight: 500,
              fontSize: 12,
              lineHeight: "16px",
              color: "rgba(242,240,237,0.5)",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {taskTitle}
          </p>
        </div>
      </div>

      {/* Meta row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 20,
          flexWrap: "wrap",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          paddingTop: 14,
        }}
      >
        <MetaItem label="Agent" value={agentName} />
        <MetaItem label="Paid" value={`$${cost.toFixed(2)}`} color="#F6AE2D" />
        <MetaItem
          label="Rating"
          value={rating != null ? <Stars filled={rating} /> : <span style={{ color: "rgba(242,240,237,0.4)" }}>skipped</span>}
        />
      </div>

      {/* Action row */}
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onNewTask}
          className="app-btn-primary"
          style={{
            flex: 1,
            background: "#8635F6",
            borderRadius: 9999,
            padding: "12px 16px",
            border: "none",
            cursor: "pointer",
            fontFamily: URB,
            fontWeight: 700,
            fontSize: 14,
            lineHeight: "20px",
            color: "#F2F0ED",
          }}
        >
          Start new task
        </button>
        {!positive && onRetry && (
          <button
            onClick={onRetry}
            className="app-btn-secondary"
            style={{
              flex: 1,
              background: "transparent",
              border: "2px solid #2E2A33",
              borderRadius: 9999,
              padding: "10px 16px",
              cursor: "pointer",
              fontFamily: URB,
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "20px",
              color: "#E5E2E1",
            }}
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

function MetaItem({
  label,
  value,
  color,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontFamily: PJK,
          fontWeight: 600,
          fontSize: 10,
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: "rgba(242,240,237,0.4)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: PJK,
          fontWeight: 600,
          fontSize: 14,
          lineHeight: "20px",
          color: color ?? "#F2F0ED",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Stars({ filled }: { filled: number }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <img
          key={n}
          src={n <= filled ? "/app-icons/star-mini.svg" : "/app-icons/star-mini-outline.svg"}
          alt=""
          style={{ width: 12, height: 12 }}
        />
      ))}
    </span>
  );
}

function Confetti() {
  const pieces = Array.from({ length: 18 });
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {pieces.map((_, i) => {
        const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
        // Deterministic pseudo-random: keeps animation consistent across renders.
        const left = ((i * 53) % 97) + 1;
        const delay = (i * 28) % 500;
        const duration = 1500 + ((i * 137) % 1000);
        return (
          <span
            key={i}
            className="app-confetti-piece"
            style={{
              left: `${left}%`,
              bottom: 0,
              background: color,
              animationDelay: `${delay}ms`,
              animationDuration: `${duration}ms`,
            }}
          />
        );
      })}
    </div>
  );
}
